/* PresencePinger — invisible component that pings /api/presence/ping every
   60s so the admin can see who is online, on which platform and page.
   Works for both authenticated and guest sessions.
*/
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const PING_MS = 60_000;
const SESSION_KEY = "orio_presence_sid";

function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `s-${Date.now()}`;
  }
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function pageSlug(pathname) {
  if (!pathname || pathname === "/") return "landing";
  if (pathname.startsWith("/app")) return `app${pathname.slice(4)}`.replace(/\/+$/, "") || "app";
  if (pathname.startsWith("/register")) return "register";
  if (pathname.startsWith("/login")) return "login";
  return pathname.replace(/^\/+/, "").split("/")[0] || "unknown";
}

export default function PresencePinger() {
  const loc = useLocation();
  const timerRef = useRef(null);

  useEffect(() => {
    const sid = getSessionId();
    const ping = () => {
      api.post("/presence/ping", {
        page: pageSlug(loc.pathname),
        standalone: detectStandalone(),
        session_id: sid,
      }).catch(() => {/* silent */});
    };
    ping();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(ping, PING_MS);
    // Ping on visibility change (user comes back)
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loc.pathname]);

  return null;
}
